// Note Expander — per-user style preferences review (feedback loop).
// Mounted as a sub-view of NoteExpanderPage (same 'noteexpander' permission
// the feedback/prefs endpoints enforce). Admins review + approve distilled
// drafts for any user; providers see their own prefs read-mostly (draft edits
// allowed on their own rows — the server enforces ownership either way).

import React, { useState, useEffect, useCallback } from 'react';
import { ccmApi } from '../lib/api.js';

const STATUS_FILTERS = ['all', 'draft', 'approved', 'retired'];

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function StatusPill({ status }) {
  const variant = status === 'approved' ? 'ok' : status === 'draft' ? 'warn' : '';
  return <span className={'pill' + (variant ? ' ' + variant : '')}>{status}</span>;
}

function PrefCard({ pref, isAdmin, onChanged }) {
  const [text, setText] = useState(pref.instructions || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null); // null | 'loading' | rows[]
  const editable = pref.status === 'draft';
  const dirty = text !== (pref.instructions || '');

  async function run(fn, label) {
    setBusy(true); setError('');
    try { await fn(); await onChanged(); }
    catch (e) { setError(`${label} failed${e && e.body && e.body.error ? `: ${e.body.error}` : '.'}`); }
    finally { setBusy(false); }
  }

  async function loadFeedback() {
    setFeedback('loading');
    try {
      const r = await ccmApi.noteExpanderFeedbackRecent({ userId: isAdmin ? pref.user_id : '', limit: 30 });
      setFeedback(r.feedback || []);
    } catch { setFeedback([]); }
  }

  return (
    <article className="ne-card" style={{ marginBottom: 12 }}>
      <div className="ne-card-head">
        <span className="ne-card-provider">{pref.user_display_name || pref.user_email || pref.user_id}</span>
        <span className="ne-card-spacer" />
        <StatusPill status={pref.status} />
        <span className="pill">v{pref.version}</span>
        {pref.distilled_at && <span className="ne-card-date">distilled {fmtWhen(pref.distilled_at)}</span>}
        {pref.approved_at && <span className="ne-card-date">approved {fmtWhen(pref.approved_at)}</span>}
      </div>

      <textarea
        value={text}
        readOnly={!editable}
        onChange={e => setText(e.target.value)}
        rows={Math.min(12, Math.max(4, text.split('\n').length + 1))}
        style={{ width: '100%', marginTop: 8, fontFamily: 'inherit', fontSize: 13,
                 padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db',
                 background: editable ? '#fff' : '#f9fafb' }}
        aria-label="Style instructions"
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {editable && (
          <button className="btn" disabled={busy || !dirty || !text.trim()}
                  onClick={() => run(() => ccmApi.noteExpanderPrefsUpdate(pref.id, text.trim()), 'Save')}>
            Save edits
          </button>
        )}
        {isAdmin && editable && (
          <button className="btn accent" disabled={busy || dirty}
                  title={dirty ? 'Save your edits first' : ''}
                  onClick={() => run(() => ccmApi.noteExpanderPrefsApprove(pref.id), 'Approve')}>
            Approve — apply to future notes
          </button>
        )}
        {isAdmin && (pref.status === 'draft' || pref.status === 'approved') && (
          <button className="btn" disabled={busy}
                  onClick={() => run(() => ccmApi.noteExpanderPrefsRetire(pref.id), 'Retire')}>
            Retire
          </button>
        )}
        <span style={{ color: '#6b7280', fontSize: 12 }}>
          from {Array.isArray(pref.source_feedback_ids) ? pref.source_feedback_ids.length : 0} feedback items
        </span>
        <button className="btn" style={{ marginLeft: 'auto' }} onClick={loadFeedback} disabled={feedback === 'loading'}>
          {feedback === 'loading' ? 'Loading…' : 'Recent feedback'}
        </button>
      </div>
      {error && <div className="ne-error" style={{ marginTop: 8 }}>{error}</div>}

      {Array.isArray(feedback) && (
        <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
          {feedback.length === 0 && <div className="ne-empty">No feedback yet.</div>}
          {feedback.map(f => (
            <div key={f.id} style={{ fontSize: 12.5, color: '#374151', padding: '4px 0' }}>
              <strong>{f.kind}</strong>
              {f.rating ? ` ${f.rating === 'up' ? '👍' : '👎'}` : ''}
              {Array.isArray(f.tags) && f.tags.length ? ` [${f.tags.join(', ')}]` : ''}
              {f.comment ? ` — “${f.comment}”` : ''}
              {f.kind === 'edit_diff' && f.payload && (f.payload.noteDeleted
                ? ' — note deleted by clinician'
                : ` — edited before signing (${f.payload.originalWords || '?'}→${f.payload.editedWords || '?'} words)`)}
              <span style={{ color: '#9ca3af' }}> · {fmtWhen(f.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export function NoteExpanderPrefsView() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [prefs, setPrefs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const r = await ccmApi.noteExpanderPrefsList({ status: statusFilter === 'all' ? '' : statusFilter });
      setPrefs(r.prefs || []);
      setIsAdmin(!!r.isAdmin);
    } catch (e) {
      setError(e && e.body && e.body.disabled
        ? 'Note Expander feedback is not enabled for this practice.'
        : 'Could not load style preferences.');
    } finally { setBusy(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 780 }}>
      <p className="sub" style={{ marginTop: 0 }}>
        Clinician feedback from Elation is distilled into per-user style instructions.
        Drafts below need review — approving one applies it to that clinician's future
        expansions (style only; it can never override grounding or safety rules).
      </p>
      <div className="ne-filter-group" role="tablist" aria-label="Prefs status" style={{ marginBottom: 12 }}>
        {STATUS_FILTERS.map(s => (
          <button key={s} type="button" role="tab" aria-selected={statusFilter === s}
                  className={'filter-pill' + (statusFilter === s ? ' active' : '')}
                  onClick={() => setStatusFilter(s)}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {error && <div className="ne-error">{error}</div>}
      {busy && prefs.length === 0 && <div className="ne-empty">Loading…</div>}
      {!busy && !error && prefs.length === 0 && (
        <div className="ne-empty">
          No {statusFilter === 'all' ? '' : statusFilter + ' '}style preferences yet — they appear
          once clinicians have given enough feedback for a distillation run.
        </div>
      )}
      {prefs.map(p => <PrefCard key={p.id} pref={p} isAdmin={isAdmin} onChanged={load} />)}
    </div>
  );
}
